import { useState } from 'react';
import {
  Check,
  Lightning,
  List,
  Moon,
  Receipt,
  SignOut,
  Storefront,
  Sun,
  Toolbox,
  type Icon,
} from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { HowItWorksMenuRow } from '@/features/welcome/components/HowItWorksButton';
import { useAuth } from '@/hooks/use-auth';
import { useExperience, type Experience } from '@/hooks/use-experience';
import { useSignOut } from '@/hooks/use-sign-out';
import { useOrders } from '../hooks/use-orders';
import { useReadyDeliveryEnabled } from '../hooks/use-ready-delivery-enabled';
import { ORDERS_PATH, READY_DELIVERY_PATH } from '../utils/slugs';

/**
 * As ações do cabeçalho, no celular.
 *
 * No desktop elas são quatro botões de ícone de 36px em fila. No celular esses
 * mesmos 36px disputavam a barra com a logo e a busca, e sobrava largura
 * insuficiente para as duas coisas: alvos apertados e um campo de busca de
 * ~100px. Aqui elas viram LINHAS de 44px com rótulo, que é o alvo que o app
 * inteiro respeita — e a busca fica com a largura da tela.
 *
 * A sacola **não** entra aqui: ela é a ação primária da vitrine e continua na
 * barra. Esconder a compra atrás de um menu seria trocar o problema de espaço
 * por um de conversão.
 *
 * Painel por baixo (`side="bottom"`), e não gaveta lateral: no celular o polegar
 * alcança a base da tela, e é o mesmo lado que a sacola e os filtros já usam.
 */
export function StorefrontMobileMenu() {
  const [aberto, setAberto] = useState(false);
  const { data } = useOrders();
  const emAndamento = data?.open.length ?? 0;
  const prontaEntrega = useReadyDeliveryEnabled();

  return (
    <Sheet open={aberto} onOpenChange={setAberto}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={
            emAndamento === 0
              ? 'Abrir o menu'
              : `Abrir o menu, ${emAndamento} pedido${
                  emAndamento === 1 ? '' : 's'
                } em andamento`
          }
        >
          <List size={20} aria-hidden />
          {/* O distintivo sobe para o gatilho porque "Meus pedidos" passou a
              viver DENTRO do menu: sem ele, um pedido a caminho não teria
              nenhum sinal na tela até alguém abrir o painel. */}
          {emAndamento > 0 && (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background"
            />
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="bottom" className="max-h-[85vh]">
        <SheetHeader>
          <SheetTitle>Menu</SheetTitle>
          <SheetDescription className="sr-only">
            Pedidos, vídeo de como funciona, tela inicial, tema e conta.
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="pb-2">
          <nav className="flex flex-col">
            {/* Mesmo critério do chip da barra: segue o toggle do banner. */}
            {prontaEntrega && (
              <Linha
                to={READY_DELIVERY_PATH}
                onNavigate={() => setAberto(false)}
                icone={Lightning}
                titulo="Pronta Entrega"
                detalhe="Estoque próprio, sai hoje"
              />
            )}
            <Linha
              to={ORDERS_PATH}
              onNavigate={() => setAberto(false)}
              icone={Receipt}
              titulo="Meus pedidos"
              detalhe={
                emAndamento > 0
                  ? `${emAndamento} em andamento`
                  : 'Acompanhe o que você comprou'
              }
            />
            <HowItWorksMenuRow />
          </nav>

          <TelaInicial />
          <Tema />
          <Conta />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function Grupo({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-4 border-t border-border pt-4">
      <h3 className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {titulo}
      </h3>
      {children}
    </section>
  );
}

/**
 * A mesma escolha do `ExperienceToggle`, em linhas.
 *
 * Some inteira quando a conta não pode alternar — é o que o toggle já fazia, e
 * um grupo com uma opção só descreveria uma escolha que não existe.
 */
function TelaInicial() {
  const { experience, canToggle, setExperience } = useExperience();
  if (!canToggle || experience === null) return null;

  return (
    <Grupo titulo="Tela inicial">
      <Opcao
        ativa={experience === 'storefront'}
        onSelect={() => setExperience('storefront')}
        icone={Storefront}
        rotulo="Vitrine"
      />
      <Opcao
        ativa={experience === 'self-service'}
        onSelect={() => setExperience('self-service' as Experience)}
        icone={Toolbox}
        rotulo="Operação"
      />
    </Grupo>
  );
}

function Tema() {
  const { theme, setTheme } = useTheme();
  const atual = (theme ?? 'system') as 'light' | 'dark' | 'system';

  return (
    <Grupo titulo="Tema">
      <Opcao
        ativa={atual === 'light'}
        onSelect={() => setTheme('light')}
        icone={Sun}
        rotulo="Claro"
      />
      <Opcao
        ativa={atual === 'dark'}
        onSelect={() => setTheme('dark')}
        icone={Moon}
        rotulo="Escuro"
      />
      {/* Sem ícone: "Sistema" não é um terceiro tema, é abrir mão da escolha —
          e um sol ou uma lua ao lado diria o contrário. */}
      <Opcao
        ativa={atual === 'system'}
        onSelect={() => setTheme('system')}
        rotulo="Sistema"
      />
    </Grupo>
  );
}

/** Por último: é a única linha do painel que encerra algo. */
function Conta() {
  const { user } = useAuth();
  const { signOut, leaving } = useSignOut();
  if (!user) return null;

  return (
    <Grupo titulo="Conta">
      <p className="truncate px-3 pb-1 text-sm text-muted-foreground">
        {user.name}
      </p>
      <Opcao
        ativa={false}
        onSelect={signOut}
        icone={SignOut}
        rotulo={leaving ? 'Saindo...' : 'Sair da conta'}
        disabled={leaving}
      />
    </Grupo>
  );
}

function Linha({
  to,
  onNavigate,
  icone: Icone,
  titulo,
  detalhe,
}: {
  to: string;
  onNavigate: () => void;
  icone: Icon;
  titulo: string;
  detalhe: string;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted"
    >
      <Icone size={20} className="shrink-0 text-primary" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-base font-medium text-foreground">
          {titulo}
        </span>
        <span className="block text-sm text-muted-foreground">{detalhe}</span>
      </span>
    </Link>
  );
}

function Opcao({
  ativa,
  onSelect,
  icone: Icone,
  rotulo,
  disabled,
}: {
  ativa: boolean;
  onSelect: () => void;
  icone?: Icon;
  rotulo: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={ativa}
      disabled={disabled}
      className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left transition-colors hover:bg-muted disabled:opacity-60"
    >
      {Icone ? (
        <Icone size={20} className="shrink-0 text-primary" aria-hidden />
      ) : (
        <span className="w-5 shrink-0" aria-hidden />
      )}
      <span className="flex-1 text-base text-foreground">{rotulo}</span>
      {ativa && <Check size={18} className="text-primary" aria-hidden />}
    </button>
  );
}
